import { fas } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup, Col, Dropdown, Form, Modal, Row, Table } from "react-bootstrap";
import ReactSelect from "react-select";
import { AutoCol, ControlledDateInput, Icon } from "../../../../component-library";
import {
    Claims,
    CreateProcedureRequest,
    Procedure,
    QueryType,
    SelectOption,
    TissueProcedureConfig,
    TissueType,
    ProcedureTypes,
    TissueTypes,
} from "../../../../models";
import { useProcedureQuery, useProcedureTypeQuery, useTissueTypeQuery } from "../../../../queries";
import { useCurrentTissue } from "../../../../hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPatch } from "rfc6902";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions, useToasts } from "../../../../contexts";
import { isTissueList } from "../../../lists";
import { getCreatedTissuesForProcedureType } from "../..";

interface ProcedureConfigModalProps {
    show: boolean;
    onHide: () => void;
    mode: "create" | "edit";
}

interface ProcedureState {
    originalTissueAction: "keep" | "change" | "";
    newTissueTypeId: number;
    createTissues: TissueProcedureConfig[];
    copyInitialGradingToPostProcGrading: boolean;
}

export function ProcedureConfigModal({ show, onHide, mode }: Readonly<ProcedureConfigModalProps>) {
    const { listSlug, territoryId } = useParams<{ listSlug: string; territoryId: string }>();
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const { errorToast } = useToasts();
    const { listQuery: tissueTypeQuery } = useTissueTypeQuery({ queryType: QueryType.List });
    const tissueTypes: TissueType[] = (tissueTypeQuery.data ?? []).filter((tt) => !tt.isResearch);
    const tissueTypeOptions: SelectOption[] = tissueTypes.map((tissueType) => ({
        label: tissueType.name,
        value: tissueType.id,
    }));
    const {
        listQuery: { data: procedureTypes },
    } = useProcedureTypeQuery();
    const { tissue, tissueId, procedure } = useCurrentTissue();
    const { createMutation, updateMutation } = useProcedureQuery({
        id: procedure?.id,
        tissueId: tissue?.id,
        caseId: tissue?.caseId,
    });
    const [procedureTypeId, setProcedureTypeId] = useState<number>(0);
    const [scheduledOn, setScheduledOn] = useState<string>("");
    const [toBeShippedOn, setToBeShippedOn] = useState<string>("");
    const [comments, setComments] = useState<string>("");
    const canCreate = hasPermission(Claims.CreateProcedure);
    const isTissue = isTissueList(listSlug);

    const listUrl = territoryId ? `/lists/${listSlug}/territory/${territoryId}` : `/lists/${listSlug}`;
    const baseUrl = isTissue
        ? `${listUrl}/tissues/${tissueId}`
        : `${listUrl}/cases/${tissue?.caseId}/tissues/${tissueId}`;

    const [procedureConfig, setProcedureConfig] = useState<ProcedureState>({
        originalTissueAction: "",
        newTissueTypeId: 0,
        createTissues: [],
        copyInitialGradingToPostProcGrading: false,
    });
    const [configLoadedForProcedureType, setConfigLoadedForProcedureType] = useState<boolean>(false);
    const selectedProcedureType = procedureTypes?.find((type) => type.id === procedureTypeId);

    useEffect(() => {
        if (configLoadedForProcedureType) return;

        let newConfig: ProcedureState = { ...procedureConfig };

        switch (selectedProcedureType?.name) {
            case ProcedureTypes.CAIRS:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Cairs)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.DMEKPatientReady:
            case ProcedureTypes.DMEKStandard:
            case ProcedureTypes.DMEKPreloaded:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Dmek)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.AsgCornea:
            case ProcedureTypes.LabCornealExcision:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Cornea)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: true,
                };
                break;
            case ProcedureTypes.MicrokeratomeALK:
            case ProcedureTypes.MicrokeratomeDSAEK:
            case ProcedureTypes.MicrokeratomeDSAEKPreloaded:
            case ProcedureTypes.MicrokeratomeDSAEKTrephined:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Precut)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.MicrokeratomeLenticule:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Lenticule)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.TissueTransferAntifungal:
            case ProcedureTypes.TissueTransferOther:
            case ProcedureTypes.TissueTransferSection:
                newConfig = {
                    originalTissueAction: "keep",
                    newTissueTypeId: 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: true,
                };
                break;
            case ProcedureTypes.ScleraPreservationWhole:
                newConfig = {
                    originalTissueAction: "change",
                    newTissueTypeId: tissueTypes?.find((tt) => tt.name === TissueTypes.Sclera)?.id ?? 0,
                    createTissues: [],
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.ScleraPreservationHalf:
                newConfig = {
                    originalTissueAction: "keep",
                    newTissueTypeId: 0,
                    createTissues: getCreatedTissuesForProcedureType(
                        ProcedureTypes.ScleraPreservationHalf,
                        tissueTypes
                    ),
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            case ProcedureTypes.ScleraPreservationQuarter:
                newConfig = {
                    originalTissueAction: "keep",
                    newTissueTypeId: 0,
                    createTissues: getCreatedTissuesForProcedureType(
                        ProcedureTypes.ScleraPreservationQuarter,
                        tissueTypes
                    ),
                    copyInitialGradingToPostProcGrading: false,
                };
                break;
            default:
                return;
        }
        setConfigLoadedForProcedureType(true);
        setProcedureConfig(newConfig);
    }, [configLoadedForProcedureType, procedureConfig, selectedProcedureType?.name, tissueTypes]);

    useEffect(() => {
        if (procedureTypeId) {
            setConfigLoadedForProcedureType(false);
        }
    }, [procedureTypeId]);

    const originalTissueConfig = useMemo(() => {
        if (procedureConfig.originalTissueAction === "change") {
            const result: TissueProcedureConfig = {
                tissueTypeId: procedureConfig.newTissueTypeId,
                copyInitialGrading: false,
            };
            return result;
        }
        return undefined;
    }, [procedureConfig.newTissueTypeId, procedureConfig.originalTissueAction]);

    useEffect(() => {
        if (!procedure?.config) return;

        if (procedure.config.originalTissueConfig) {
            setProcedureConfig({
                originalTissueAction: "change",
                newTissueTypeId: procedure.config.originalTissueConfig.tissueTypeId,
                createTissues: procedure.config.createTissues,
                copyInitialGradingToPostProcGrading: procedure.config.copyInitialGradingToPostProcGrading,
            });
            setComments(procedure.comments || "");
        } else {
            setProcedureConfig({
                originalTissueAction: "keep",
                newTissueTypeId: 0,
                createTissues: procedure.config.createTissues,
                copyInitialGradingToPostProcGrading: procedure.config.copyInitialGradingToPostProcGrading,
            });
            setComments(procedure.comments || "");
        }
    }, [procedure]);

    const removeNewTissue = (index: number) => {
        setProcedureConfig((prev) => ({
            ...prev,
            createTissues: prev.createTissues.filter((_, i) => i !== index),
        }));
    };

    const addQuarters = useCallback(() => {
        const quarterTypes = tissueTypes.filter((tt) => tt.name.includes("Quarter"));
        setProcedureConfig((prev) => ({
            ...prev,
            createTissues: [
                ...prev.createTissues,
                ...quarterTypes.map((tt) => ({
                    tissueTypeId: tt.id,
                    copyInitialGrading: false,
                })),
            ],
        }));
    }, [tissueTypes]);

    const addSplitCornea = useCallback(() => {
        const cornea1Type = tissueTypes.find((tt) => tt.name.includes("Cornea1"));
        const cornea2Type = tissueTypes.find((tt) => tt.name.includes("Cornea2"));
        if (cornea1Type && cornea2Type) {
            setProcedureConfig((prev) => ({
                ...prev,
                createTissues: [
                    ...prev.createTissues,
                    { tissueTypeId: cornea1Type.id, copyInitialGrading: false },
                    { tissueTypeId: cornea2Type.id, copyInitialGrading: false },
                ],
            }));
        }
    }, [tissueTypes]);

    const addSplitPrecut = useCallback(() => {
        const precut1Type = tissueTypes.find((tt) => tt.name.includes("Precut1"));
        const precut2Type = tissueTypes.find((tt) => tt.name.includes("Precut2"));
        if (precut1Type && precut2Type) {
            setProcedureConfig((prev) => ({
                ...prev,
                createTissues: [
                    ...prev.createTissues,
                    { tissueTypeId: precut1Type.id, copyInitialGrading: false },
                    { tissueTypeId: precut2Type.id, copyInitialGrading: false },
                ],
            }));
        }
    }, [tissueTypes]);

    const addSplitSclera = useCallback(() => {
        const sclera1Type = tissueTypes.find((tt) => tt.name.includes("Sclera1"));
        const sclera2Type = tissueTypes.find((tt) => tt.name.includes("Sclera2"));
        if (sclera1Type && sclera2Type) {
            setProcedureConfig((prev) => ({
                ...prev,
                createTissues: [
                    ...prev.createTissues,
                    { tissueTypeId: sclera1Type.id, copyInitialGrading: false },
                    { tissueTypeId: sclera2Type.id, copyInitialGrading: false },
                ],
            }));
        }
    }, [tissueTypes]);

    const update = async () => {
        if (!procedure) return;
        const updatedProcedure: Procedure = {
            ...procedure,
            config: {
                originalTissueConfig,
                createTissues: procedureConfig.createTissues,
                copyInitialGradingToPostProcGrading: procedureConfig.copyInitialGradingToPostProcGrading,
            },
        };
        const patch = createPatch(procedure, updatedProcedure);
        try {
            await updateMutation.mutateAsync(patch);
            onHide();
        } catch (error: unknown) {
            errorToast(error);
        }
    };

    const create = async () => {
        if (!canCreate || !procedureTypeId) return;
        const procedure: Partial<Procedure> = {
            procedureTypeId,
            scheduledOn,
            toBeShippedOn,
            config: {
                originalTissueConfig,
                createTissues: procedureConfig.createTissues,
                copyInitialGradingToPostProcGrading: procedureConfig.copyInitialGradingToPostProcGrading,
            },
            comments: comments,
        };
        const request: CreateProcedureRequest = {
            tissueId: Number(tissueId),
            procedure,
        };
        try {
            const response = await createMutation.mutateAsync(request);
            navigate(`${baseUrl}/procedures/${response.id}`);
        } catch (error: unknown) {
            errorToast(error);
        }
    };

    const save = () => (mode === "create" ? create() : update());

    return (
        <Modal show={show} onHide={onHide} centered size="xl">
            <Modal.Header closeButton>
                <Modal.Title>{mode === "create" ? "New Procedure" : "Procedure Configuration"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {mode === "create" && (
                    <Row>
                        <AutoCol className="mb-3">
                            <Form.Group controlId="procedureType">
                                <Form.Label>Procedure Type</Form.Label>
                                <Form.Select
                                    value={procedureTypeId}
                                    onChange={(e) => setProcedureTypeId(Number(e.target.value))}
                                >
                                    <option value={0} disabled>
                                        Select a procedure type...
                                    </option>
                                    {procedureTypes?.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </AutoCol>
                        <AutoCol className="mb-3">
                            <ControlledDateInput
                                label="Scheduled On"
                                value={scheduledOn}
                                onChange={(e) => setScheduledOn(e?.toISOString() ?? "")}
                            />
                        </AutoCol>
                        <AutoCol className="mb-3">
                            <ControlledDateInput
                                label="Shipping Time"
                                withTime
                                value={toBeShippedOn}
                                onChange={(e) => setToBeShippedOn(e?.toISOString() ?? "")}
                            />
                        </AutoCol>
                    </Row>
                )}
                <Row>
                    <Col className="mb-2">
                        <div>Original Tissues</div>
                        <div className="d-flex align-items-center">
                            <Form.Check
                                type="radio"
                                name="original-tissue"
                                id="original-tissue-change"
                                label="Change to type:"
                                checked={procedureConfig.originalTissueAction === "change"}
                                onChange={() =>
                                    setProcedureConfig((prev) => ({
                                        ...prev,
                                        originalTissueAction: "change",
                                    }))
                                }
                                className="me-2"
                            />
                            <ReactSelect
                                options={tissueTypeOptions}
                                value={tissueTypeOptions.find(
                                    (tissueType) => tissueType.value === procedureConfig.newTissueTypeId
                                )}
                                onChange={(option) =>
                                    setProcedureConfig((prev) => ({
                                        ...prev,
                                        newTissueTypeId: Number(option?.value),
                                    }))
                                }
                                placeholder="Select a tissue type..."
                                isDisabled={!!procedure?.isFinal}
                            />
                        </div>
                        <Form.Check
                            type="radio"
                            name="original-tissue"
                            id="original-tissue-keep"
                            label="No change"
                            checked={procedureConfig.originalTissueAction === "keep"}
                            className="mb-3"
                            onChange={() =>
                                setProcedureConfig((prev) => ({
                                    ...prev,
                                    originalTissueAction: "keep",
                                }))
                            }
                        />
                        <Form.Check
                            type="checkbox"
                            id="copy-initial-to-post-proc"
                            label="Copy initial grading to post-procedure grading"
                            checked={procedureConfig.copyInitialGradingToPostProcGrading}
                            onChange={() =>
                                setProcedureConfig((prev) => ({
                                    ...prev,
                                    copyInitialGradingToPostProcGrading: !prev.copyInitialGradingToPostProcGrading,
                                }))
                            }
                        />
                    </Col>
                    <Col>
                        <div>Create Tissues</div>
                        {!procedureConfig.createTissues.length && <p className="text-muted">(none)</p>}

                        <Dropdown as={ButtonGroup} className="mb-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                    setProcedureConfig((prev) => ({
                                        ...prev,
                                        createTissues: [
                                            ...prev.createTissues,
                                            { tissueTypeId: 0, copyInitialGrading: false },
                                        ],
                                    }))
                                }
                            >
                                <Icon icon={fas.faPlus} /> New Tissue
                            </Button>

                            <Dropdown.Toggle split variant="outline-primary" id="new-tissue-dropdown" />

                            <Dropdown.Menu>
                                <Dropdown.Item onClick={addQuarters}>Quarters</Dropdown.Item>
                                <Dropdown.Item onClick={addSplitCornea}>Split Cornea</Dropdown.Item>
                                <Dropdown.Item onClick={addSplitPrecut}>Split Precut</Dropdown.Item>
                                <Dropdown.Item onClick={addSplitSclera}>Split Sclera</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Table borderless size="sm">
                            <tbody>
                                {procedureConfig.createTissues.map((newTissue: TissueProcedureConfig, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                title="Remove tissue"
                                                onClick={() => removeNewTissue(index)}
                                                className="border-0"
                                            >
                                                <Icon icon={fas.faTrash} />
                                            </Button>
                                        </td>
                                        <td>
                                            <ReactSelect
                                                options={tissueTypeOptions}
                                                value={tissueTypeOptions.find(
                                                    (tissueType) => tissueType.value === newTissue.tissueTypeId
                                                )}
                                                onChange={(option) =>
                                                    setProcedureConfig((prev) => ({
                                                        ...prev,
                                                        createTissues: prev.createTissues.map((tissue, i) =>
                                                            i === index
                                                                ? {
                                                                      ...tissue,
                                                                      tissueTypeId: Number(option?.value),
                                                                  }
                                                                : tissue
                                                        ),
                                                    }))
                                                }
                                                placeholder="Select a tissue type..."
                                                isDisabled={!!procedure?.isFinal}
                                            />
                                        </td>
                                        <td>
                                            <Form.Check
                                                id={`copy-initial-grading-data-${index}`}
                                                type="checkbox"
                                                label={
                                                    <span>
                                                        Copy initial grading{" "}
                                                        {index === 0 && (
                                                            <Icon
                                                                icon={fas.faInfoCircle}
                                                                className="text-primary"
                                                                title="Copy source tissue initial grading to new tissue initial grading"
                                                                aria-label="Copy source tissue initial grading to new tissue initial grading"
                                                            />
                                                        )}
                                                    </span>
                                                }
                                                checked={newTissue.copyInitialGrading}
                                                onChange={() =>
                                                    setProcedureConfig((prev) => ({
                                                        ...prev,
                                                        createTissues: prev.createTissues.map((tissue, i) =>
                                                            i === index
                                                                ? {
                                                                      ...tissue,
                                                                      copyInitialGrading: !tissue.copyInitialGrading,
                                                                  }
                                                                : tissue
                                                        ),
                                                    }))
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                    <Col xs={12}>
                        <Form.Group>
                            <Form.Label>Comments</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                placeholder="Add comments..."
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={save}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
